/**
 * StorageUtil - Centralized utility for browser storage operations
 * 
 * Features:
 * - Type-safe storage operations with error handling
 * - Supports both localStorage and sessionStorage
 * - Automatic JSON serialization/deserialization
 * - Fallback mechanisms for when storage is unavailable
 * - Cache management with TTL (Time To Live) support
 * - Batch operations for multiple keys
 * - Storage size monitoring
 * - Migration utilities for legacy keys
 * 
 * Usage Examples:
 * 
 * Basic operations:
 * StorageUtil.setConfig('theme', 'dark');
 * const theme = StorageUtil.getConfig('theme', 'light');
 * 
 * Session storage:
 * StorageUtil.setSession('temp-data', { foo: 'bar' });
 * const data = StorageUtil.getSession('temp-data', {});
 * 
 * Cache with TTL:
 * StorageUtil.setCache('api-data', data, 60000); // 1 minute TTL
 * const cachedData = StorageUtil.getCache('api-data', []);
 * 
 * Batch operations:
 * StorageUtil.setBatch({ key1: 'value1', key2: 'value2' });
 * StorageUtil.removeBatch(['key1', 'key2']);
 * 
 * Storage management:
 * const size = StorageUtil.getStorageSize(); // Size in bytes
 * StorageUtil.cleanupExpiredCache(); // Remove expired cache entries
 */

export enum StorageType {
  LOCAL = 'localStorage',
  SESSION = 'sessionStorage'
}

export class StorageUtil {
  // Storage keys constants
  public static readonly KEYS = {
    // App Configuration
    APP_CONFIG: 'app-configuration',
    LANGUAGE: 'language',
    CURRENCY: 'currency',
    REGION: 'region',
    THEME: 'theme',
    
    // User Preferences
    VIEW_MODE: 'view-mode',
    ITEMS_PER_PAGE: 'items-per-page',
    FILTERS: 'filters',
    SORT_PREFERENCES: 'sort-preferences',
    
    // UI State
    SIDEBAR_COLLAPSED: 'sidebar-collapsed',
    CART_ITEMS: 'cart-items',
    RECENT_SEARCHES: 'recent-searches',
    
    // Cache
    PRODUCTS_CACHE: 'products-cache',
    SCHOOLS_CACHE: 'schools-cache',
    EVENTS_CACHE: 'events-cache',
    MINISTRIES_CACHE: 'ministries-cache',
  } as const;

  /**
   * Check if storage is available
   */
  private static isStorageAvailable(type: StorageType): boolean {
    try {
      const storage = window[type];
      const testKey = '__storage_test__';
      storage.setItem(testKey, 'test');
      storage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get storage instance
   */
  private static getStorage(type: StorageType): Storage | null {
    if (!this.isStorageAvailable(type)) {
      console.warn(`${type} is not available`);
      return null;
    }
    return window[type];
  }

  /**
   * Set item in storage with type safety
   */
  public static setItem<T>(
    key: string, 
    value: T, 
    type: StorageType = StorageType.LOCAL
  ): boolean {
    try {
      const storage = this.getStorage(type);
      if (!storage) return false;

      const serializedValue = typeof value === 'string' 
        ? value 
        : JSON.stringify(value);
      
      storage.setItem(key, serializedValue);
      return true;
    } catch (error) {
      console.error(`Failed to set ${key} in ${type}:`, error);
      return false;
    }
  }

  /**
   * Get item from storage with type safety and fallback
   */
  public static getItem<T>(
    key: string, 
    fallback: T, 
    type: StorageType = StorageType.LOCAL
  ): T {
    try {
      const storage = this.getStorage(type);
      if (!storage) return fallback;

      const item = storage.getItem(key);
      if (item === null) return fallback;

      // Try to parse as JSON, fallback to string
      try {
        return JSON.parse(item);
      } catch {
        return item as T;
      }
    } catch (error) {
      console.error(`Failed to get ${key} from ${type}:`, error);
      return fallback;
    }
  }

  /**
   * Get item from storage as string
   */
  public static getString(
    key: string, 
    fallback: string = '', 
    type: StorageType = StorageType.LOCAL
  ): string {
    try {
      const storage = this.getStorage(type);
      if (!storage) return fallback;

      return storage.getItem(key) ?? fallback;
    } catch (error) {
      console.error(`Failed to get string ${key} from ${type}:`, error);
      return fallback;
    }
  }

  /**
   * Remove item from storage
   */
  public static removeItem(
    key: string, 
    type: StorageType = StorageType.LOCAL
  ): boolean {
    try {
      const storage = this.getStorage(type);
      if (!storage) return false;

      storage.removeItem(key);
      return true;
    } catch (error) {
      console.error(`Failed to remove ${key} from ${type}:`, error);
      return false;
    }
  }

  /**
   * Clear all storage
   */
  public static clear(type: StorageType = StorageType.LOCAL): boolean {
    try {
      const storage = this.getStorage(type);
      if (!storage) return false;

      storage.clear();
      return true;
    } catch (error) {
      console.error(`Failed to clear ${type}:`, error);
      return false;
    }
  }

  /**
   * Get all keys from storage
   */
  public static getAllKeys(type: StorageType = StorageType.LOCAL): string[] {
    try {
      const storage = this.getStorage(type);
      if (!storage) return [];

      const keys: string[] = [];
      for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i);
        if (key) keys.push(key);
      }
      return keys;
    } catch (error) {
      console.error(`Failed to get all keys from ${type}:`, error);
      return [];
    }
  }

  /**
   * Check if key exists in storage
   */
  public static hasKey(
    key: string, 
    type: StorageType = StorageType.LOCAL
  ): boolean {
    try {
      const storage = this.getStorage(type);
      if (!storage) return false;

      return storage.getItem(key) !== null;
    } catch (error) {
      console.error(`Failed to check key ${key} in ${type}:`, error);
      return false;
    }
  }

  /**
   * Get storage size in bytes (approximate)
   */
  public static getStorageSize(type: StorageType = StorageType.LOCAL): number {
    try {
      const storage = this.getStorage(type);
      if (!storage) return 0;

      let totalSize = 0;
      for (let key in storage) {
        if (storage.hasOwnProperty(key)) {
          totalSize += key.length + (storage[key]?.length || 0);
        }
      }
      return totalSize;
    } catch (error) {
      console.error(`Failed to get storage size for ${type}:`, error);
      return 0;
    }
  }

  /**
   * Batch operations
   */
  public static setBatch<T extends Record<string, any>>(
    items: T,
    type: StorageType = StorageType.LOCAL
  ): boolean {
    try {
      const storage = this.getStorage(type);
      if (!storage) return false;

      Object.entries(items).forEach(([key, value]) => {
        this.setItem(key, value, type);
      });
      return true;
    } catch (error) {
      console.error(`Failed to set batch items in ${type}:`, error);
      return false;
    }
  }

  /**
   * Remove multiple keys
   */
  public static removeBatch(
    keys: string[], 
    type: StorageType = StorageType.LOCAL
  ): boolean {
    try {
      keys.forEach(key => this.removeItem(key, type));
      return true;
    } catch (error) {
      console.error(`Failed to remove batch items from ${type}:`, error);
      return false;
    }
  }

  /**
   * Utility methods for common operations
   */
  
  // Configuration storage helpers
  public static setConfig<T>(key: string, value: T): boolean {
    return this.setItem(key, value, StorageType.LOCAL);
  }

  public static getConfig<T>(key: string, fallback: T): T {
    return this.getItem(key, fallback, StorageType.LOCAL);
  }

  // Session storage helpers
  public static setSession<T>(key: string, value: T): boolean {
    return this.setItem(key, value, StorageType.SESSION);
  }

  public static getSession<T>(key: string, fallback: T): T {
    return this.getItem(key, fallback, StorageType.SESSION);
  }

  // Cache management
  public static setCache<T>(key: string, value: T, ttl?: number): boolean {
    const cacheItem = {
      value,
      timestamp: Date.now(),
      ttl: ttl || (24 * 60 * 60 * 1000) // Default 24 hours
    };
    return this.setItem(key, cacheItem, StorageType.LOCAL);
  }

  public static getCache<T>(key: string, fallback: T): T {
    const cacheItem = this.getItem(key, null, StorageType.LOCAL);
    
    if (!cacheItem || typeof cacheItem !== 'object') {
      return fallback;
    }

    const { value, timestamp, ttl } = cacheItem as any;
    const now = Date.now();
    
    // Check if cache has expired
    if (timestamp && ttl && (now - timestamp > ttl)) {
      this.removeItem(key, StorageType.LOCAL);
      return fallback;
    }

    return value !== undefined ? value : fallback;
  }

  /**
   * Migration utility for existing storage keys
   */
  public static migrateKey(
    oldKey: string, 
    newKey: string, 
    type: StorageType = StorageType.LOCAL
  ): boolean {
    try {
      if (this.hasKey(oldKey, type)) {
        const value = this.getItem(oldKey, null, type);
        if (value !== null) {
          this.setItem(newKey, value, type);
          this.removeItem(oldKey, type);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error(`Failed to migrate key from ${oldKey} to ${newKey}:`, error);
      return false;
    }
  }

  /**
   * Clean up expired cache entries
   */
  public static cleanupExpiredCache(): void {
    const keys = this.getAllKeys(StorageType.LOCAL);
    keys.forEach(key => {
      if (key.endsWith('_cache') || key.includes('cache')) {
        // Try to get cache item to trigger expiration check
        this.getCache(key, null);
      }
    });
  }
}

export default StorageUtil;